import { ReactNode, useEffect, useState } from 'react';

interface StickyButtonWrapperProps {
  children: ReactNode;
  shadowClass?: string;
  containerClasses?: string;
  scrollContainer?: string | 'window';
  stickyElementSelector?: string; // For window scroll detection
}

/**
 * A wrapper component that displays sticky buttons with a shadow effect based on scroll position.
 *
 * This wrapper was created in order to solve the issue of a conditional class needed to be added
 * to the button container when the user scrolls. The buttons needed to be displayed over the UI, with a
 * shadow effect to indicate that there is more content to scroll to.
 * It was necessary to split the logic into 2 parts.
 *    1. To detect the scroll position when the container is positioned in the document and measure the scroll in the window.
 *    2. To grab and detect the scroll on a container element which is scrollable (likely using overflow-y-auto) and contains the buttons which also need the shadow effect.
 *
 *  The component from the design reference: https://www.figma.com/design/C9dQntNquXhJLBQgcVOUYa/Amana-branded---OLR-redesign?node-id=16272-10567&m=dev
 *
 * @component
 * @example
 * // For window scroll
 * <StickyButtonWrapper
 *   scrollContainer="window"
 *   stickyElementSelector=".my-sticky-button"
 * >
 *   <button>Submit</button>
 * </StickyButtonWrapper>
 *
 * @example
 * // For container scroll
 * <StickyButtonWrapper
 *   scrollContainer=".modal-content"
 *   containerClasses="sticky bottom-0 bg-white"
 * >
 *   <button>Save</button> // or any container element that needs to be positioned over content and then merge with UI at a given point.
 * </StickyButtonWrapper>
 *
 * @param {StickyButtonWrapperProps} props - The component props
 * @param {React.ReactNode} props.children - The button content to be wrapped
 * @param {string} [props.containerClasses='sticky bottom-0 bg-white'] - CSS classes applied to the wrapper container
 * @param {string} [props.scrollContainer='.overflow-y-auto'] - Selector for the scroll container, or 'window' for document scroll
 * @param {string} [props.stickyElementSelector] - Selector for the element whose position determines shadow visibility (required when scrollContainer is 'window')
 * @returns {JSX.Element} A sticky positioned div containing the children with conditional shadow styling
 */
export const StickyButtonWrapper = ({
  children,
  containerClasses = 'sticky bottom-0 bg-white',
  scrollContainer = '.overflow-y-auto', // Assuming container has been made scrollable
  stickyElementSelector,
  shadowClass = 'shadow-[0px_-4px_4px_0px_rgba(0,0,0,0.25)]',
}: StickyButtonWrapperProps) => {
  const [showShadow, setShowShadow] = useState(false);

  useEffect(() => {
    if (scrollContainer === 'window') {
      const handleWindowScroll = () => {
        if (stickyElementSelector) {
          const element = document.querySelector(stickyElementSelector);
          if (element) {
            const buttonContainer = element.getBoundingClientRect();
            const scrollIsAtBottom = buttonContainer.bottom < window.innerHeight - 40;
            setShowShadow(!scrollIsAtBottom);
          }
        }
      };

      handleWindowScroll();
      window.addEventListener('scroll', handleWindowScroll);
      return () => {
        window.removeEventListener('scroll', handleWindowScroll);
      };
    } else {
      const parentContainer = document.querySelector(scrollContainer);
      if (parentContainer) {
        const scroll = () => {
          const { scrollTop, scrollHeight, clientHeight } = parentContainer as HTMLElement;
          const scrollIsAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
          setShowShadow(!scrollIsAtBottom);
        };

        scroll();
        parentContainer.addEventListener('scroll', scroll);
        return () => {
          parentContainer.removeEventListener('scroll', scroll);
        };
      }
    }
  }, [scrollContainer, stickyElementSelector]);

  return (
    <div className={`${containerClasses} ${showShadow ? shadowClass : ''}`.trim()}>{children}</div>
  );
};
